import axios from "axios";
import { createClient } from "../util/axios-helpers";
import { endpoint } from "../util/http-helpers";

export interface AvailabilityResponse {
    fromDate: string;
    availableUntil: string | null;
    hasNextReservation: boolean;
    availableDays: number;
}

export const getAvailability = (fromDate?: string) => {
    const client = createClient(axios);
    const queryParams = fromDate ? `?fromDate=${fromDate}` : '';
    return client.get<AvailabilityResponse>(endpoint(`api/v1/reservations/availability${queryParams}`));
};

export interface EarlyCheckInAvailabilityResponse {
    arrivalDate: string;
    availableFrom: string | null;
    hasPreviousReservation: boolean;
    availableDays: number;
}

export const getEarlyCheckInAvailability = () => {
    const client = createClient(axios);
    return client.get<EarlyCheckInAvailabilityResponse>(endpoint('api/v1/reservations/early-checkin/availability'));
};

export interface CreateExtensionRequestResponse {
    extensionRequestId: string;
    clientSecret: string;
    totalPriceCents: number;
    numberOfNights: number;
    pricePerNightCents: number;
}

export interface CreateExtensionRequest {
    requestedDepartureTimestamp: number;
}

export const createExtensionRequest = (request: CreateExtensionRequest) => {
    const client = createClient(axios);
    return client.post<CreateExtensionRequestResponse>(endpoint('api/v1/reservations/extend'), request);
};

export interface PendingExtensionRequest {
    id: string;
    requestedDepartureTimestamp: number;
    numberOfNights: number;
    estimatedPriceCents: number;
    currency: string;
    status: string;
    createDate: string;
}

export const getPendingExtensionRequest = () => {
    const client = createClient(axios);
    return client.get<PendingExtensionRequest | null>(endpoint('api/v1/reservations/extend/pending'));
};

// Get the latest extension request regardless of status (Pending, Approved, Rejected)
export const getLatestExtensionRequest = () => {
    const client = createClient(axios);
    return client.get<PendingExtensionRequest | null>(endpoint('api/v1/reservations/extend/latest'));
};

// Get all extension requests for the current reservation
export const getMyExtensionRequests = () => {
    const client = createClient(axios);
    return client.get<PendingExtensionRequest[]>(endpoint('api/v1/me/extension-requests'));
};

// Upgrade pricing types and API
export interface UpgradePriceOption {
    code: string;
    label: string;
    hours: number;
    priceCents: number;
    isEnabled: boolean;
}

export interface ServicePriceOption {
    code: string;
    label: string;
    priceCents: number;
    frequency: string;
    photoUrl: string | null;
    isEnabled: boolean;
}

export interface UpgradePricingResponse {
    currency: string;
    earlyCheckinPrices: UpgradePriceOption[];
    lateCheckoutPrices: UpgradePriceOption[];
    extensionPricePerNightCents: number | null;
    servicePrices: ServicePriceOption[];
    averageDailyRate: number | null;
    lastUpdated: string | null;
}

export const getUpgradePricing = () => {
    const client = createClient(axios);
    return client.get<UpgradePricingResponse>(endpoint('api/v1/reservations/upgrade-prices'));
};
